import type { Preset } from '@openng/optimus-ui-themes/types';
import Aura from '@openng/optimus-ui-themes/aura';
import Nora from '@openng/optimus-ui-themes/nora';
import { definePreset } from '@openng/optimus-ui-themes';
import {
  darkSurfacePalette,
  lightSurfacePalette,
  primaryPalette,
} from './palettes';

/**
 * Dark scheme restated against the inverted ramp in `darkSurfacePalette`.
 *
 * Every value here is Aura's own dark token with its surface reference mirrored
 * (950 -> 0, 900 -> 50, 800 -> 100, 700 -> 200, 600 -> 300, 0 -> 950), so the
 * components keep Aura's contrast relationships while the app's `surface-*`
 * Tailwind classes flip with the scheme.
 */
const darkColorScheme = {
  surface: darkSurfacePalette,
  primary: {
    color: '{primary.400}',
    contrastColor: '{surface.0}',
    hoverColor: '{primary.300}',
    activeColor: '{primary.200}',
  },
  highlight: {
    background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
    focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
    color: '{surface.950}',
    focusColor: '{surface.950}',
  },
  mask: {
    background: 'rgba(0,0,0,0.6)',
    color: '{surface.800}',
  },
  formField: {
    background: '{surface.0}',
    disabledBackground: '{surface.200}',
    filledBackground: '{surface.100}',
    filledHoverBackground: '{surface.100}',
    filledFocusBackground: '{surface.100}',
    borderColor: '{surface.300}',
    hoverBorderColor: '{surface.400}',
    focusBorderColor: '{primary.color}',
    invalidBorderColor: '{red.300}',
    color: '{surface.950}',
    disabledColor: '{surface.500}',
    placeholderColor: '{surface.600}',
    invalidPlaceholderColor: '{red.400}',
    floatLabelColor: '{surface.600}',
    floatLabelFocusColor: '{primary.color}',
    floatLabelActiveColor: '{surface.600}',
    floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
    iconColor: '{surface.600}',
    shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.25)',
  },
  text: {
    color: '{surface.950}',
    hoverColor: '{surface.950}',
    mutedColor: '{surface.600}',
    hoverMutedColor: '{surface.700}',
  },
  content: {
    background: '{surface.50}',
    hoverBackground: '{surface.100}',
    borderColor: '{surface.200}',
    color: '{text.color}',
    hoverColor: '{text.hover.color}',
  },
  overlay: {
    select: {
      background: '{surface.50}',
      borderColor: '{surface.200}',
      color: '{text.color}',
    },
    popover: {
      background: '{surface.50}',
      borderColor: '{surface.200}',
      color: '{text.color}',
    },
    modal: {
      background: '{surface.50}',
      borderColor: '{surface.200}',
      color: '{text.color}',
    },
  },
  list: {
    option: {
      focusBackground: '{surface.100}',
      selectedBackground: '{highlight.background}',
      selectedFocusBackground: '{highlight.focus.background}',
      color: '{text.color}',
      focusColor: '{text.hover.color}',
      selectedColor: '{highlight.color}',
      selectedFocusColor: '{highlight.focus.color}',
      icon: { color: '{surface.500}', focusColor: '{surface.600}' },
    },
    optionGroup: { background: 'transparent', color: '{text.muted.color}' },
  },
  navigation: {
    item: {
      focusBackground: '{surface.100}',
      activeBackground: '{surface.100}',
      color: '{text.color}',
      focusColor: '{text.hover.color}',
      activeColor: '{text.hover.color}',
      icon: {
        color: '{surface.500}',
        focusColor: '{surface.600}',
        activeColor: '{surface.600}',
      },
    },
    submenuLabel: { background: 'transparent', color: '{text.muted.color}' },
    submenuIcon: {
      color: '{surface.500}',
      focusColor: '{surface.600}',
      activeColor: '{surface.600}',
    },
  },
};

const progressSpinnerColors = {
  colorOne: primaryPalette['400'],
  colorTwo: primaryPalette['500'],
  colorThree: primaryPalette['600'],
  colorFour: primaryPalette['700'],
};

/**
 * Aura with warm surfaces, a visible focus ring, tighter list density, softer
 * overlay shadows and a working dark scheme.
 */
export const exosourcePreset = definePreset(Aura, {
  semantic: {
    primary: primaryPalette,
    // Slightly larger radii than stock Aura (md 6px -> 8px). Reads as current
    // without tipping into the fully rounded look that dates quickly.
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '8px',
      lg: '12px',
      xl: '16px',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      borderRadius: '{border.radius.md}',
      // Stock Aura sets this to width 0, so a focused field only changes its
      // border colour. A soft primary halo is both more legible and the
      // current convention.
      focusRing: {
        width: '2px',
        style: 'solid',
        color: 'color-mix(in srgb, {primary.color}, transparent 75%)',
        offset: '0',
        shadow: 'none',
      },
    },
    list: {
      padding: '0.25rem',
      gap: '2px',
      option: { padding: '0.375rem 0.625rem', borderRadius: '{border.radius.sm}' },
      optionGroup: { padding: '0.375rem 0.625rem', fontWeight: '600' },
    },
    navigation: {
      list: { padding: '0.25rem', gap: '2px' },
      item: { padding: '0.375rem 0.625rem', borderRadius: '{border.radius.sm}' },
    },
    // Larger blur at lower opacity, instead of Aura's tight two-step shadow.
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow:
          '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.06)',
      },
      popover: {
        borderRadius: '{border.radius.lg}',
        padding: '0.75rem',
        shadow:
          '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.06)',
      },
      modal: {
        borderRadius: '{border.radius.lg}',
        padding: '1.25rem',
        shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      },
    },
    colorScheme: {
      light: { surface: lightSurfacePalette },
      dark: darkColorScheme,
    },
  },
  components: {
    progressspinner: {
      colorScheme: {
        light: { root: progressSpinnerColors },
        dark: { root: progressSpinnerColors },
      },
    },
  },
});

/** Stock Aura, brand primary only — the app's look before this change. */
export const auraPreset = definePreset(Aura, {
  semantic: { primary: primaryPalette },
  components: {
    progressspinner: {
      colorScheme: { light: { root: progressSpinnerColors } },
    },
  },
});

/** Stock Nora, brand primary only — sharper and denser, for comparison. */
export const noraPreset = definePreset(Nora, {
  semantic: { primary: primaryPalette },
  components: {
    progressspinner: {
      colorScheme: { light: { root: progressSpinnerColors } },
    },
  },
});

/** Matches `darkModeSelector` in `app.config.ts`. */
export const DARK_MODE_CLASS = 'app-dark';

export type ThemePresetId = 'exosource' | 'aura' | 'nora';

export interface ThemePresetOption {
  id: ThemePresetId;
  label: string;
  description: string;
  /** Only the custom preset defines a dark colour scheme. */
  supportsDark: boolean;
  preset: Preset;
}

export const THEME_PRESETS: readonly ThemePresetOption[] = [
  {
    id: 'exosource',
    label: 'Exosource',
    description: 'Warm surfaces, focus rings, tighter lists',
    // Dark mode is switched off until the review view is fixed for it; the
    // dark scheme stays defined so flipping this back is all it takes.
    supportsDark: false,
    preset: exosourcePreset,
  },
  {
    id: 'aura',
    label: 'Aura',
    description: 'Stock Aura — the previous look',
    supportsDark: false,
    preset: auraPreset,
  },
  {
    id: 'nora',
    label: 'Nora',
    description: 'Stock Nora — sharp corners, dense, solid highlights',
    supportsDark: false,
    preset: noraPreset,
  },
];

export const DEFAULT_THEME_PRESET_ID: ThemePresetId = 'exosource';
